import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
  FaLock,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { educationReports } from "../utils/api";
import { exportEducationReport } from "../utils/educationReportExport";
import { useToast } from "../contexts/ToastContext";
import ConfirmationModal from "../components/ConfirmationModal";

const statusStyles = {
  draft: "bg-gray-100 text-gray-700",
  submitted: "bg-amber-100 text-amber-800",
  locked: "bg-green-100 text-green-800",
};

const emptyResult = {
  selected: false,
  resultType: "marks",
  marksObtained: "",
  maximumMarks: "",
  grade: "",
  isAbsent: false,
  isFailed: false,
};

const createResultDrafts = (report) => {
  const drafts = {};
  for (const year of report?.academicYears || []) {
    for (const semester of year.semesters || []) {
      const subjects = semester.selectableSubjects || semester.subjects || [];
      for (const exam of semester.exams || []) {
        const existing = new Map(exam.results.map((result) => [Number(result.subjectId), result]));
        drafts[exam.id] = {};
        for (const subject of subjects) {
          const result = existing.get(Number(subject.id));
          drafts[exam.id][subject.id] = {
            selected: Boolean(result),
            resultType: result?.resultType || "marks",
            marksObtained: result?.marksObtained == null ? "" : String(result.marksObtained),
            maximumMarks: result?.maximumMarks == null ? "" : String(result.maximumMarks),
            grade: result?.grade || "",
            isAbsent: Boolean(result?.isAbsent),
            isFailed: Boolean(result?.isFailed),
          };
        }
      }
    }
  }
  return drafts;
};

const createGpaDrafts = (report) => {
  const drafts = {};
  for (const year of report?.academicYears || []) {
    for (const semester of year.semesters || []) {
      drafts[semester.id] = {
        sgpa: semester.sgpa == null ? "" : String(semester.sgpa),
        cgpa: semester.cgpa == null ? "" : String(semester.cgpa),
      };
    }
  }
  return drafts;
};

const EducationReportPage = ({ user }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const showErrorRef = useRef(showError);
  const targetStudentId = studentId ? Number(studentId) : Number(user?.id);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [institution, setInstitution] = useState({ collegeName: "", universityName: "" });
  const [academicYear, setAcademicYear] = useState({ startYear: "", endYear: "" });
  const [semesterNames, setSemesterNames] = useState({});
  const [subjectForms, setSubjectForms] = useState({});
  const [examForms, setExamForms] = useState({});
  const [resultDrafts, setResultDrafts] = useState({});
  const [gpaDrafts, setGpaDrafts] = useState({});
  const [verificationNotes, setVerificationNotes] = useState({});
  const [examToLock, setExamToLock] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedSemesters, setExpandedSemesters] = useState({});
  const [expandedExams, setExpandedExams] = useState({});

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadReport = useCallback(async (withSpinner = true) => {
    if (!targetStudentId) return;
    if (withSpinner) setLoading(true);
    try {
      const response = await educationReports.get(targetStudentId);
      setReport(response.data);
      setInstitution({
        collegeName: response.data.profile?.collegeName || "",
        universityName: response.data.profile?.universityName || "",
      });
      setResultDrafts(createResultDrafts(response.data));
      setGpaDrafts(createGpaDrafts(response.data));
      setExpandedYears((current) => Object.fromEntries(
        response.data.academicYears.map((year, index) => [year.id, current[year.id] ?? index === 0])
      ));
      setExpandedSemesters((current) => Object.fromEntries(
        response.data.academicYears.flatMap((year) =>
          year.semesters.map((semester, index) => [semester.id, current[semester.id] ?? index === 0])
        )
      ));
      setExpandedExams((current) => Object.fromEntries(
        response.data.academicYears.flatMap((year) => year.semesters.flatMap((semester) =>
          semester.exams.map((exam, index) => [exam.id, current[exam.id] ?? index === 0])
        ))
      ));
    } catch (error) {
      showErrorRef.current(error.response?.data?.error || "Failed to load the education report");
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, [targetStudentId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const runAction = async (key, action, successMessage) => {
    setSavingKey(key);
    try {
      const response = await action();
      showSuccess(response.data?.message || successMessage);
      await loadReport(false);
      return true;
    } catch (error) {
      showError(error.response?.data?.error || "Could not update the education report");
      return false;
    } finally {
      setSavingKey("");
    }
  };

  const allSemesters = useMemo(
    () => report?.academicYears.flatMap((year) => year.semesters) || [],
    [report]
  );

  const addSemester = async (event, yearId) => {
    event.preventDefault();
    const saved = await runAction(
      `semester-${yearId}`,
      () => educationReports.createSemester(yearId, { name: semesterNames[yearId] || "" }),
      "Semester created"
    );
    if (saved) setSemesterNames((current) => ({ ...current, [yearId]: "" }));
  };

  const addSubject = async (event, semesterId) => {
    event.preventDefault();
    const values = subjectForms[semesterId] || { name: "", code: "" };
    const saved = await runAction(
      `subject-${semesterId}`,
      () => educationReports.addSubject(semesterId, values),
      "Subject added"
    );
    if (saved) setSubjectForms((current) => ({ ...current, [semesterId]: { name: "", code: "" } }));
  };

  const addExam = async (event, semesterId) => {
    event.preventDefault();
    const form = examForms[semesterId] || {};
    const values = {
      type: form.type || "midsem",
      name: form.name || "",
    };
    const saved = await runAction(
      `exam-${semesterId}`,
      () => educationReports.addExam(semesterId, values),
      "Exam added"
    );
    if (saved) setExamForms((current) => ({
      ...current,
      [semesterId]: { type: "midsem", name: "" },
    }));
  };

  const deleteItem = (message, key, action) => {
    const itemType = key.startsWith("delete-exam-")
      ? "exam"
      : key.startsWith("delete-subject-")
        ? "subject"
        : key.startsWith("delete-semester-")
          ? "semester"
          : "academic year";
    setDeleteConfirmation({ message, key, action, itemType });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    const { key, action, itemType } = deleteConfirmation;
    await runAction(key, action, `${itemType} deleted`);
  };

  const updateResult = (examId, subjectId, field, value) => {
    setResultDrafts((current) => ({
      ...current,
      [examId]: {
        ...(current[examId] || {}),
        [subjectId]: {
          ...emptyResult,
          ...(current[examId]?.[subjectId] || {}),
          [field]: value,
        },
      },
    }));
  };

  const saveExamResults = async (exam, subjects) => {
    const results = subjects
      .map((subject) => ({ subjectId: subject.id, ...emptyResult, ...(resultDrafts[exam.id]?.[subject.id] || {}) }))
      .filter((result) => result.selected)
      .map(({ selected, ...result }) => result);
    await runAction(
      `results-${exam.id}`,
      () => educationReports.saveResults(exam.id, results),
      `${exam.name} results saved`
    );
  };

  const saveGpa = (semesterId) => runAction(
    `gpa-${semesterId}`,
    () => educationReports.saveGpa(semesterId, gpaDrafts[semesterId] || {}),
    "SGPA and CGPA saved"
  );

  const lockExam = async () => {
    if (!examToLock) return;
    await runAction(
      `lock-${examToLock.id}`,
      () => educationReports.verifyAndLockExam(examToLock.id, verificationNotes[examToLock.id] || ""),
      "Exam verified and locked"
    );
    setExamToLock(null);
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-lg">Loading education report...</div>;
  if (!report) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-600">Education report could not be loaded.</div>;

  const canEdit = report.canEdit;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-10">
      <main className="max-w-6xl mx-auto px-4">
        {studentId && (
          <button onClick={() => navigate(`/student-profile/${studentId}`)} className="flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <FaArrowLeft className="mr-2" /> Back to Student Profile
          </button>
        )}

        <div className="bg-white rounded-lg shadow p-5 md:p-7 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Education Report</h1>
              <p className="text-gray-600 mt-1">{report.student.firstName} {report.student.lastName}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!canEdit && <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">Review mode</span>}
              {report.canExport && (
                <button type="button" onClick={() => exportEducationReport(report)} className="inline-flex items-center px-4 py-2 border border-green-600 text-green-700 rounded hover:bg-green-50">
                  <FaDownload className="mr-2" /> Export to Excel
                </button>
              )}
            </div>
          </div>

          <form onSubmit={(event) => {
            event.preventDefault();
            runAction("institution", () => educationReports.saveProfile(institution), "Institution details saved");
          }} className="mt-6 border-t pt-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">College & University</h2>
              {report.institutionLocked && <span className="inline-flex items-center text-sm text-green-700"><FaLock className="mr-1" /> Fixed after verification</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required maxLength="255" aria-label="College name" placeholder="College name" value={institution.collegeName} onChange={(event) => setInstitution((current) => ({ ...current, collegeName: event.target.value }))} disabled={!canEdit || report.institutionLocked} className="w-full px-3 py-2 border rounded disabled:bg-gray-100" />
              <input required maxLength="255" aria-label="University name" placeholder="University name" value={institution.universityName} onChange={(event) => setInstitution((current) => ({ ...current, universityName: event.target.value }))} disabled={!canEdit || report.institutionLocked} className="w-full px-3 py-2 border rounded disabled:bg-gray-100" />
            </div>
            {canEdit && !report.institutionLocked && <button type="submit" disabled={savingKey === "institution"} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Save Institution Details</button>}
          </form>
        </div>

        {canEdit && report.profile && (
          <form onSubmit={(event) => {
            event.preventDefault();
            runAction("academic-year", () => educationReports.createAcademicYear(academicYear), "Academic year created").then((saved) => {
              if (saved) setAcademicYear({ startYear: "", endYear: "" });
            });
          }} className="bg-white rounded-lg shadow p-5 mb-6">
            <h2 className="text-lg font-semibold mb-3">Add Academic Year</h2>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <input type="number" required min="1950" max="2100" placeholder="Start year" value={academicYear.startYear} onChange={(event) => setAcademicYear((current) => ({ ...current, startYear: event.target.value }))} className="px-3 py-2 border rounded" />
              <input type="number" required min="1950" max="2102" placeholder="End year" value={academicYear.endYear} onChange={(event) => setAcademicYear((current) => ({ ...current, endYear: event.target.value }))} className="px-3 py-2 border rounded" />
              <button type="submit" className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded"><FaPlus className="mr-2" /> Add Year</button>
            </div>
          </form>
        )}

        {!report.profile && !canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4">The student has not started their education report yet.</div>}

        {report.academicYears.map((year) => (
          <section key={year.id} className="bg-white rounded-lg shadow mb-6 overflow-hidden">
            <div className="bg-blue-50 border-b px-5 py-4 flex items-center justify-between gap-3">
              <button type="button" onClick={() => setExpandedYears((current) => ({ ...current, [year.id]: !current[year.id] }))} className="flex flex-1 items-center gap-3 text-left">
                {expandedYears[year.id] ? <FaChevronUp /> : <FaChevronDown />}
                <h2 className="text-xl font-bold">Academic Year {year.label}</h2>
              </button>
              {canEdit && !year.semesters.some((semester) => semester.hasLockedExams) && (
                <button type="button" title="Delete academic year" onClick={() => deleteItem(`Delete academic year ${year.label} and all of its data?`, `delete-year-${year.id}`, () => educationReports.deleteAcademicYear(year.id))} className="p-2 text-red-600"><FaTrash /></button>
              )}
            </div>

            {expandedYears[year.id] && <div className="p-5 space-y-6">
              {canEdit && (
                <form onSubmit={(event) => addSemester(event, year.id)} className="flex gap-3 items-end">
                  <input required maxLength="100" placeholder="Semester name" value={semesterNames[year.id] || ""} onChange={(event) => setSemesterNames((current) => ({ ...current, [year.id]: event.target.value }))} className="flex-1 px-3 py-2 border rounded" />
                  <button type="submit" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded"><FaPlus className="mr-2" /> Add Semester</button>
                </form>
              )}
              {year.semesters.length === 0 && <p className="text-gray-500 text-sm">No semesters have been added.</p>}

              {year.semesters.map((semester) => {
                const hasEndSemester = semester.exams.some((exam) => exam.type === "end_semester");
                return (
                  <article key={semester.id} className="border rounded-lg overflow-hidden">
                    <header className="bg-gray-50 border-b p-4 flex items-center justify-between gap-3">
                      <button type="button" onClick={() => setExpandedSemesters((current) => ({ ...current, [semester.id]: !current[semester.id] }))} className="flex flex-1 items-center gap-3 text-left">
                        {expandedSemesters[semester.id] ? <FaChevronUp /> : <FaChevronDown />}
                        <h3 className="text-lg font-semibold">{semester.name}</h3>
                      </button>
                      {canEdit && !semester.hasLockedExams && <button type="button" onClick={() => deleteItem(`Delete ${semester.name} and all subjects and results?`, `delete-semester-${semester.id}`, () => educationReports.deleteSemester(semester.id))} className="text-sm text-red-600">Delete semester</button>}
                    </header>

                    {expandedSemesters[semester.id] && <div className="p-4 space-y-6">
                      <div>
                        <h4 className="font-semibold mb-3">Semester Subjects</h4>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {semester.subjects.map((subject) => (
                            <span key={subject.id} className="inline-flex items-center bg-gray-100 rounded-full px-3 py-1.5 text-sm">
                              {subject.code ? `${subject.code} — ` : ""}{subject.name}
                              {subject.isBacklog && <span className="ml-2 text-xs font-semibold text-red-600">Backlog</span>}
                              {canEdit && <button type="button" aria-label={`Delete ${subject.name}`} onClick={() => deleteItem(`Delete ${subject.name} and its unlocked results?`, `delete-subject-${subject.id}`, () => educationReports.deleteSubject(subject.id))} className="ml-2 text-red-500">×</button>}
                            </span>
                          ))}
                          {semester.subjects.length === 0 && <span className="text-sm text-gray-500">No subjects added yet.</span>}
                        </div>
                        {canEdit && (
                          <form onSubmit={(event) => addSubject(event, semester.id)} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
                            <input maxLength="50" placeholder="Code (optional)" value={subjectForms[semester.id]?.code || ""} onChange={(event) => setSubjectForms((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), code: event.target.value } }))} className="px-3 py-2 border rounded" />
                            <input required maxLength="200" placeholder="Subject name" value={subjectForms[semester.id]?.name || ""} onChange={(event) => setSubjectForms((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), name: event.target.value } }))} className="px-3 py-2 border rounded" />
                            <button type="submit" className="px-4 py-2 border border-blue-600 text-blue-600 rounded">Add Subject</button>
                          </form>
                        )}
                      </div>

                      <div>
                        <h4 className="font-semibold mb-3">Exams & Results</h4>
                        {canEdit && (
                          <form onSubmit={(event) => addExam(event, semester.id)} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2 mb-5">
                            <select value={examForms[semester.id]?.type || "midsem"} onChange={(event) => setExamForms((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), type: event.target.value } }))} className="px-3 py-2 border rounded">
                              <option value="midsem">Internal / Midsem</option>
                              <option value="end_semester" disabled={hasEndSemester}>End Semester</option>
                            </select>
                            <input required maxLength="150" placeholder="Exam name (e.g. Unit Test 3)" value={examForms[semester.id]?.name || ""} onChange={(event) => setExamForms((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), name: event.target.value } }))} className="px-3 py-2 border rounded" />
                            <button type="submit" className="px-4 py-2 border border-blue-600 text-blue-600 rounded">Add Exam</button>
                          </form>
                        )}
                        {semester.exams.length === 0 && <p className="text-sm text-gray-500">No exams have been added.</p>}

                        <div className="space-y-5">
                          {semester.exams.map((exam) => {
                            const examLocked = exam.status === "locked";
                            const examEditable = canEdit && !examLocked;
                            const subjects = examEditable
                              ? semester.selectableSubjects
                              : semester.selectableSubjects.filter((subject) => resultDrafts[exam.id]?.[subject.id]?.selected);
                            return (
                              <div key={exam.id} className="border rounded overflow-hidden">
                                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between gap-3">
                                  <button type="button" onClick={() => setExpandedExams((current) => ({ ...current, [exam.id]: !current[exam.id] }))} className="flex flex-1 items-center gap-2 text-left">
                                    {expandedExams[exam.id] ? <FaChevronUp /> : <FaChevronDown />}
                                    <h5 className="font-medium">{exam.name}</h5>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusStyles[exam.status]}`}>{exam.status}</span>
                                  </button>
                                  {examEditable && <button type="button" onClick={() => deleteItem(`Delete ${exam.name} and all of its results?`, `delete-exam-${exam.id}`, () => educationReports.deleteExam(exam.id))} className="text-red-600 text-sm">Delete</button>}
                                </div>

                                {expandedExams[exam.id] && <div>
                                  {exam.status === "submitted" && canEdit && <div className="m-4 bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">Awaiting verification. Saving changes returns only this exam to Draft.</div>}
                                  {examLocked && <div className="m-4 bg-green-50 border border-green-200 text-green-800 rounded p-3 text-sm"><div className="font-medium flex items-center"><FaLock className="mr-2" /> Verified and locked</div>{exam.verifiedBy && <div className="mt-1">Verified by {exam.verifiedBy}</div>}{exam.verificationNotes && <div className="mt-1">Notes: {exam.verificationNotes}</div>}</div>}
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                      <thead><tr className="border-b text-left text-gray-600">
                                        {examEditable && <th className="px-3 py-2">Include</th>}
                                        <th className="px-3 py-2">Subject</th><th className="px-3 py-2">Result type</th><th className="px-3 py-2">Obtained</th><th className="px-3 py-2">Maximum</th><th className="px-3 py-2">Grade</th><th className="px-3 py-2">Absent</th>{exam.type === "end_semester" && <th className="px-3 py-2">Failed</th>}<th className="px-3 py-2">%</th>
                                      </tr></thead>
                                      <tbody>
                                        {subjects.map((subject) => {
                                          const draft = { ...emptyResult, ...(resultDrafts[exam.id]?.[subject.id] || {}) };
                                          const disabled = !examEditable || !draft.selected;
                                          const percentage = draft.resultType === "marks" && draft.maximumMarks && !draft.isAbsent && draft.marksObtained !== "" ? ((Number(draft.marksObtained) / Number(draft.maximumMarks)) * 100).toFixed(1) : "—";
                                          return <tr key={subject.id} className="border-b last:border-b-0">
                                            {examEditable && <td className="px-3 py-2 text-center"><input type="checkbox" checked={draft.selected} onChange={(event) => updateResult(exam.id, subject.id, "selected", event.target.checked)} /></td>}
                                            <td className="px-3 py-2 font-medium whitespace-nowrap">{subject.code ? `${subject.code} — ` : ""}{subject.name}{subject.isBacklog && <span className="ml-2 text-xs font-semibold text-red-600">Backlog{subject.semesterName && Number(subject.semesterId) !== Number(semester.id) ? ` · ${subject.semesterName}` : ""}</span>}</td>
                                            <td className="px-3 py-2"><select value={draft.resultType} disabled={disabled} onChange={(event) => updateResult(exam.id, subject.id, "resultType", event.target.value)} className="w-24 px-2 py-1.5 border rounded disabled:bg-gray-100"><option value="marks">Marks</option><option value="grade">Grade</option></select></td>
                                            <td className="px-3 py-2">{draft.resultType === "marks" ? <input type="number" min="0" step="0.01" value={draft.marksObtained} disabled={disabled || draft.isAbsent} onChange={(event) => updateResult(exam.id, subject.id, "marksObtained", event.target.value)} className="w-24 px-2 py-1.5 border rounded disabled:bg-gray-100" /> : "—"}</td>
                                            <td className="px-3 py-2">{draft.resultType === "marks" ? <input type="number" min="0.01" step="0.01" value={draft.maximumMarks} disabled={disabled} onChange={(event) => updateResult(exam.id, subject.id, "maximumMarks", event.target.value)} className="w-24 px-2 py-1.5 border rounded disabled:bg-gray-100" /> : "—"}</td>
                                            <td className="px-3 py-2">{draft.resultType === "grade" ? <input type="text" maxLength="20" value={draft.grade} disabled={disabled || draft.isAbsent} onChange={(event) => updateResult(exam.id, subject.id, "grade", event.target.value.toUpperCase())} className="w-20 px-2 py-1.5 border rounded uppercase disabled:bg-gray-100" /> : "—"}</td>
                                            <td className="px-3 py-2 text-center"><input type="checkbox" checked={draft.isAbsent} disabled={disabled} onChange={(event) => updateResult(exam.id, subject.id, "isAbsent", event.target.checked)} /></td>
                                            {exam.type === "end_semester" && <td className="px-3 py-2 text-center"><input type="checkbox" checked={draft.isFailed} disabled={disabled} onChange={(event) => updateResult(exam.id, subject.id, "isFailed", event.target.checked)} /></td>}
                                            <td className="px-3 py-2">{percentage}{percentage !== "—" ? "%" : ""}</td>
                                          </tr>;
                                        })}
                                      </tbody>
                                    </table>
                                  </div>

                                  {exam.type === "end_semester" && <div className="border-t bg-blue-50 px-4 py-4">
                                    <p className="text-sm text-blue-800 mb-3">Failed subjects are carried forward as backlogs after this exam is verified.</p>
                                    <div className="flex flex-wrap items-end gap-3">
                                      <label className="text-sm">SGPA<input type="number" min="0" max="10" step="0.01" value={gpaDrafts[semester.id]?.sgpa || ""} disabled={!examEditable} onChange={(event) => setGpaDrafts((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), sgpa: event.target.value } }))} className="block w-28 px-3 py-2 border rounded disabled:bg-gray-100" /></label>
                                      <label className="text-sm">CGPA<input type="number" min="0" max="10" step="0.01" value={gpaDrafts[semester.id]?.cgpa || ""} disabled={!examEditable} onChange={(event) => setGpaDrafts((current) => ({ ...current, [semester.id]: { ...(current[semester.id] || {}), cgpa: event.target.value } }))} className="block w-28 px-3 py-2 border rounded disabled:bg-gray-100" /></label>
                                      {examEditable && <button type="button" onClick={() => saveGpa(semester.id)} className="px-4 py-2 bg-blue-600 text-white rounded">Save SGPA & CGPA</button>}
                                    </div>
                                  </div>}

                                  {examEditable && <div className="px-4 py-3 bg-gray-50 border-t flex flex-wrap gap-3">
                                    <button type="button" disabled={savingKey === `results-${exam.id}`} onClick={() => saveExamResults(exam, semester.selectableSubjects)} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Save Selected Results</button>
                                    {exam.status !== "submitted" && <button type="button" disabled={!exam.isComplete || savingKey === `submit-${exam.id}`} onClick={() => runAction(`submit-${exam.id}`, () => educationReports.submitExam(exam.id), `${exam.name} submitted`)} className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-300">Submit This Exam for Verification</button>}
                                    {!exam.isComplete && <span className="self-center text-sm text-gray-500">Save at least one complete result{exam.type === "end_semester" ? " and SGPA/CGPA" : ""} first.</span>}
                                  </div>}

                                  {report.canVerify && exam.status === "submitted" && <div className="border-t p-4">
                                    <textarea rows="2" maxLength="500" value={verificationNotes[exam.id] || ""} onChange={(event) => setVerificationNotes((current) => ({ ...current, [exam.id]: event.target.value }))} className="w-full px-3 py-2 border rounded mb-3" placeholder="Verification notes (optional)" />
                                    <button type="button" onClick={() => setExamToLock(exam)} className="inline-flex items-center px-5 py-2.5 bg-green-600 text-white rounded"><FaLock className="mr-2" /> Verify & Lock This Exam</button>
                                  </div>}
                                </div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>}
                  </article>
                );
              })}
            </div>}
          </section>
        ))}

        {report.profile && allSemesters.length === 0 && !canEdit && <div className="bg-white rounded-lg shadow p-5 text-gray-500">The student has saved institution details but has not added any semesters.</div>}
      </main>

      <ConfirmationModal
        isOpen={Boolean(deleteConfirmation)}
        onClose={() => setDeleteConfirmation(null)}
        onConfirm={confirmDelete}
        title={`Delete ${deleteConfirmation?.itemType || "item"}?`}
        message={deleteConfirmation?.message || "Are you sure you want to delete this item?"}
        confirmText="Delete"
        cancelText="Cancel"
        confirmStyle="bg-red-600 hover:bg-red-700 text-white"
        icon="🗑️"
      />

      <ConfirmationModal
        isOpen={Boolean(examToLock)}
        onClose={() => setExamToLock(null)}
        onConfirm={lockExam}
        title="Permanently lock exam?"
        message={`You are confirming that you manually verified ${examToLock?.name || "this exam"}. Once locked, its selected subjects and results cannot be changed.`}
        confirmText="Verify & Lock"
        confirmStyle="bg-green-600 hover:bg-green-700 text-white"
        icon="🔒"
      />
    </div>
  );
};

export default EducationReportPage;
